/* shared/forms — record create/edit form drawer + company picker.
   Extracted from services.jsx: useDirtyForm, RecordFormDrawer, FormField,
   CompanyPicker, QuickCompanyModal. ESM.

   NOTE: FORM_SCHEMAS / submit read window.__nx_store for live company/people
   lookups. That mirror is set by shared/state's StoreProvider, so it is an
   internal shared coupling (preserved from the prototype), not a tier leak. */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NexusIcon } from '../ui/NexusIcon';
import { Btn } from '../ui';
import { useStore } from '../state';
import { useToast } from '../feedback';
import { NEXUS_DATA } from '../data';

const isBlank = (v) => v == null || String(typeof v === 'string' ? v.trim() : v).length === 0;

/* ---------- Phone validation (E.164-compatible, no extra dependency) ---------- */
const normalizePhoneRaw = (raw) => raw.replace(/[\s\-().]/g, '');
export const validatePhone = (raw) => {
  if (!raw || !raw.trim()) return null;
  const normalized = normalizePhoneRaw(raw.trim());
  if (!/^\+[1-9]\d{6,14}$/.test(normalized))
    return 'Enter a valid phone number with country code (e.g. +91 9876543210).';
  return null;
};

/* ---------- useDirtyForm ---------- */
export const useDirtyForm = (initial) => {
  const [values, setValues] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const prevKeyRef = useRef(null);
  useEffect(() => {
    const key = JSON.stringify(initial);
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      setValues(initial);
      setDirty(false);
    }
  });
  const setField = useCallback((field, value) => {
    setValues(v => ({ ...v, [field]: value }));
    setDirty(true);
  }, []);
  return { values, setValues, dirty, setDirty, setField };
};

/* ---------- Form schemas ---------- */
const FORM_SCHEMAS = {
  people: [
    { field: 'salutation', label: 'Salutation', type: 'select', options: ['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.'], defaultValue: '' },
    { field: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'e.g. Sarah Jenkins' },
    { field: 'email', label: 'Email', type: 'email', required: true, placeholder: 'sarah@company.com' },
    { field: 'phone', label: 'Phone', type: 'phone', placeholder: '+1 (000) 000-0000' },
    { field: 'role', label: 'Role', type: 'text', placeholder: 'Head of Product' },
    { field: 'companyId', label: 'Company', type: 'company-picker', required: true, placeholder: 'Search companies…' },
    { field: 'status', label: 'Status', type: 'select', options: ['New', 'Engaged', 'Qualified', 'Customer'], defaultValue: 'New' },
    { field: 'score', label: 'Score', type: 'number', defaultValue: 50 },
    { field: 'owner', label: 'Owner', type: 'select', options: () => { const names = window.__nx_store?.memberNames; const me = window.__nx_auth?.user?.name; return names?.length ? names : (me ? [me] : ['You']); }, defaultValue: () => window.__nx_auth?.user?.name || '' },
    { field: 'city', label: 'City', type: 'text' },
    { field: 'tags', label: 'Tags', type: 'tags', defaultValue: [] },
  ],
  companies: [
    { field: 'name',            label: 'Company Name',     type: 'text',              required: true },
    { field: 'lifecycle',       label: 'Lifecycle',         type: 'select',            options: ['Lead', 'Account', 'Enterprise'], defaultValue: 'Lead' },
    { field: 'parentCompanyId', label: 'Parent Enterprise', type: 'enterprise-picker', showWhen: (v) => v.lifecycle === 'Account' },
    { field: 'domain',          label: 'Domain',            type: 'text',              placeholder: 'acme.com' },
    { field: 'industry',        label: 'Industry',          type: 'select',            options: ['Software', 'Fintech', 'BFSI', 'Commerce', 'Media', 'Design Tools', 'CRM', 'Healthcare', 'Manufacturing', 'Other'] },
    { field: 'employees',       label: 'Employees',         type: 'number',            defaultValue: 0 },
    { field: 'revenue',         label: 'Revenue',           type: 'text',              placeholder: '$10M' },
    { field: 'location',        label: 'Location',          type: 'text' },
    { field: 'score',           label: 'Company Score',     type: 'number',            defaultValue: 50, min: 0, max: 100, hint: '0–20 Low Quality  ·  21–50 Average  ·  51–80 Good Prospect  ·  81–100 Highly Qualified' },
    { field: 'owner',           label: 'Owner',             type: 'select',            options: () => { const names = window.__nx_store?.memberNames; const me = window.__nx_auth?.user?.name; return names?.length ? names : (me ? [me] : ['You']); }, defaultValue: () => window.__nx_auth?.user?.name || '' },
    { field: 'tags',            label: 'Tags',              type: 'tags',              defaultValue: [] },
  ],
  deals: [
    { field: 'title',       label: 'Deal Title',        type: 'text',     required: true },
    { field: 'value',       label: 'Value',              type: 'number',   required: true, defaultValue: 10000 },
    { field: 'currency',    label: 'Currency',           type: 'select',   options: ['USD', 'EUR', 'GBP', 'INR', 'AED', 'SGD', 'AUD'], defaultValue: 'USD' },
    { field: 'pipelineId',  label: 'Pipeline',           type: 'select',   required: true, createOnly: true,
      options: () => (window.__nx_store?.pipelines || []).map(p => ({ value: p.id, label: p.name })),
      defaultValue: () => window.__nx_store?.activePipelineId || window.__nx_store?.pipelines?.[0]?.id || '' },
    { field: 'stageId',     label: 'Stage',              type: 'select',   required: true,
      options: (values) => {
        const pid = values?.pipelineId;
        const all = window.__nx_store?.allStages || [];
        const filtered = pid ? all.filter(s => s.pipelineId === pid) : all;
        if (filtered.length) return filtered.map(s => ({ value: s.id, label: s.name }));
        const stgs = window.__nx_store?.pipeline?.length ? window.__nx_store.pipeline : NEXUS_DATA.stages;
        return stgs.map(s => ({ value: s.uuid || s.id, label: s.name }));
      },
      defaultValue: () => {
        const pid = window.__nx_store?.activePipelineId || window.__nx_store?.pipelines?.[0]?.id;
        const all = window.__nx_store?.allStages || [];
        const first = pid ? all.find(s => s.pipelineId === pid) : all[0];
        return first?.id || window.__nx_store?.pipeline?.[0]?.uuid || window.__nx_store?.pipeline?.[0]?.id || '';
      } },
    { field: 'companyId',   label: 'Company',            type: 'select',   options: () => window.__nx_store.companies.map(c => ({ value: c.id, label: c.name })) },
    { field: 'contactId',   label: 'Primary Contact',    type: 'select',   options: () => window.__nx_store.people.map(p => ({ value: p.id, label: p.name })) },
    { field: 'priority',    label: 'Priority',           type: 'select',   options: ['High', 'Medium', 'Low'], defaultValue: 'Medium' },
    { field: 'closeDate',   label: 'Expected Close',     type: 'date' },
    { field: 'owner',       label: 'Owner',              type: 'select',   options: () => { const names = window.__nx_store?.memberNames; const me = window.__nx_auth?.user?.name; if (names?.length) { return names; } return me ? [me] : ['You']; }, defaultValue: () => { const name = window.__nx_auth?.user?.name; const names = window.__nx_store?.memberNames; return (name && names?.includes(name)) ? name : ''; } },
    { field: 'tags',        label: 'Tags',               type: 'text',     placeholder: 'comma-separated' },
    { field: 'description', label: 'Description',        type: 'textarea' },
  ],
  tasks: [
    { field: 'title',     label: 'Title',       type: 'text',     required: true, placeholder: 'Send proposal' },
    { field: 'priority',  label: 'Priority',    type: 'select',   options: ['Urgent', 'High', 'Medium', 'Low'], defaultValue: 'Medium' },
    { field: 'dueDate',   label: 'Due Date',    type: 'date' },
    { field: 'assignee',  label: 'Assignee',    type: 'select',   options: () => window.__nx_store?.memberNames || [] },
    { field: 'notes',     label: 'Description', type: 'textarea', placeholder: 'Add a description…' },
    { field: 'personId',  label: 'Contact',  type: 'select', options: () => window.__nx_store?.people?.map(p => ({ value: p.id, label: p.name })) || [] },
    { field: 'companyId', label: 'Company',  type: 'company-picker' },
    { field: 'dealId',    label: 'Deal',     type: 'select', options: () => (window.__nx_store?.deals || []).map(d => ({ value: d.id, label: d.title })) },
  ],
};

const buildInitial = (schema, prefill = {}) => {
  const init = {};
  schema.forEach(f => {
    if (prefill[f.field] !== undefined) init[f.field] = prefill[f.field];
    else if (f.defaultValue !== undefined) init[f.field] = typeof f.defaultValue === 'function' ? f.defaultValue() : f.defaultValue;
    else init[f.field] = f.type === 'tags' ? [] : '';
  });
  // Pass through prefill keys not covered by schema (e.g. dealId for task context)
  const schemaFields = new Set(schema.map(f => f.field));
  Object.keys(prefill).forEach(k => { if (!schemaFields.has(k)) init[k] = prefill[k]; });
  return init;
};

/* ---------- Context-field helpers (UX-009) ---------- */
const getCtxDisplayValue = (fieldName, value, store) => {
  if (!value) return '';
  switch (fieldName) {
    case 'contactId':
    case 'personId':
      return store.people?.find(p => p.id === value)?.name || value;
    case 'companyId':
      return store.companies?.find(c => c.id === value)?.name || value;
    case 'dealId':
      return store.deals?.find(d => d.id === value)?.title || value;
    case 'owner':
    case 'assignee':
      return value;
    default:
      return String(value);
  }
};

/* ---------- RecordFormDrawer ---------- */
export const RecordFormDrawer = () => {
  const store = useStore();
  const isCreate = !!store.createForm;
  const isEdit = !!store.editForm;
  const open = isCreate || isEdit;
  const type = isCreate ? store.createForm.type : isEdit ? store.editForm.type : null;
  const recordIn = isCreate ? store.createForm.prefill : isEdit ? store.editForm.record : {};
  const schema = type ? FORM_SCHEMAS[type] : null;
  const context = isCreate ? (recordIn?._context || null) : null;

  const initial = useMemo(() => schema ? buildInitial(schema, recordIn) : {}, [schema, recordIn]);
  const form = useDirtyForm(initial);
  const [errors, setErrors] = useState({});
  const activeSchema = useMemo(
    () => (schema ? schema.filter(f => (!f.showWhen || f.showWhen(form.values)) && (!f.createOnly || isCreate)) : []),
    [schema, form.values, isCreate],
  );

  const close = useCallback(() => {
    if (form.dirty) {
      store.confirm({
        title: 'Discard changes?',
        description: 'You have unsaved edits in this form.',
        confirmLabel: 'Discard',
        danger: true,
        onConfirm: () => { isCreate ? store.closeCreateForm() : store.closeEditForm(); },
      });
      return;
    }
    isCreate ? store.closeCreateForm() : store.closeEditForm();
  }, [form.dirty, isCreate, store]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); close(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, close]);

  if (!open) return null;

  const submit = () => {
    const errs = {};
    activeSchema.forEach(f => {
      if (f.required && isBlank(form.values[f.field])) errs[f.field] = 'Required';
      if (f.type === 'number' && (f.min != null || f.max != null)) {
        const v = Number(form.values[f.field]);
        if (form.values[f.field] !== '' && form.values[f.field] != null) {
          if (f.min != null && v < f.min) errs[f.field] = `Must be ${f.min} or above`;
          if (f.max != null && v > f.max) errs[f.field] = `Must be ${f.max} or below`;
        }
      }
    });
    if (type === 'people' && form.values.phone && form.values.phone.trim()) {
      const phoneChanged = isCreate || form.values.phone.trim() !== (recordIn?.phone || '').trim();
      if (phoneChanged) {
        const phoneErr = validatePhone(form.values.phone.trim());
        if (phoneErr) errs.phone = phoneErr;
      }
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;

    if (isCreate) {
      const payload = { ...form.values };
      if (type === 'people') {
        payload.tone = '#125AB0';
        payload.avatar = (payload.name || '').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
        payload.lastActivity = 'just now';
        if (typeof payload.tags === 'string') payload.tags = payload.tags.split(',').map(s => s.trim()).filter(Boolean);
        if (payload.companyId) {
          const c = (window.__nx_store.companies || []).find(x => x.id === payload.companyId);
          if (c) payload.company = c.name;
        }
      }
      if (type === 'companies') {
        payload.deals = 0; payload.lastActivity = 'just now';
        if (typeof payload.tags === 'string') payload.tags = payload.tags.split(',').map(s => s.trim()).filter(Boolean);
        if (!payload.parentCompanyId) payload.parentCompanyId = null;
      }
      if (type === 'deals') {
        payload.value = Number(payload.value) || 0;
        payload.age = 0;
        payload.lastTouch = 'just now';
        if (typeof payload.tags === 'string') payload.tags = payload.tags.split(',').map(s => s.trim()).filter(Boolean);
      }
      store.create(type, payload);
      store.closeCreateForm();
    } else {
      const patch = { ...form.values };
      if (type === 'deals') {
        patch.value = Number(patch.value) || 0;
        delete patch.archived;
      }
      if (type === 'people' && patch.companyId) {
        const c = (window.__nx_store.companies || []).find(x => x.id === patch.companyId);
        if (c) patch.company = c.name;
      }
      if (type === 'companies') {
        if (typeof patch.tags === 'string') patch.tags = patch.tags.split(',').map(s => s.trim()).filter(Boolean);
        if (!patch.parentCompanyId) patch.parentCompanyId = null;
      }
      store.update(type, recordIn.id, patch);
      store.closeEditForm();
    }
  };

  const titleMap = { people: 'Person', companies: 'Company', deals: 'Deal', tasks: 'Task' };
  const verb = isCreate ? 'New' : 'Edit';
  return (
    <>
      <div className="nx-drawer-veil" onClick={close} style={{ zIndex: 79 }} />
      <aside className="nx-drawer nx-form-drawer" style={{ zIndex: 80 }}>
        <div className="nx-drawer-head">
          <div className="nx-drawer-head-top">
            <button className="nx-drawer-prev" onClick={close}><NexusIcon name="X" size={14} /></button>
            <div className="nx-drawer-breadcrumb">
              <span>{verb}</span>
              <NexusIcon name="ChevronRight" size={11} />
              <span className="is-current">{titleMap[type]}</span>
            </div>
            {form.dirty && <span className="nx-dirty-dot" title="Unsaved changes" />}
          </div>
          <div className="nx-form-drawer-hero">
            <h2 className="nx-drawer-title">{verb} {titleMap[type]}</h2>
            <span className="nx-drawer-sub">{isCreate ? 'Fill in the details below.' : 'Update the fields and save.'}</span>
          </div>
        </div>
        <div className="nx-drawer-body">
          <div className="nx-form-grid">
            {activeSchema.map(f => {
              const isLocked = isCreate && !!context?.readOnly?.includes(f.field) && !!form.values[f.field];

              let effectiveField = f;
              if (!isLocked && f.field === 'dealId' && context?.personId) {
                const personDeals = (store.deals || []).filter(d => d.contactId === context.personId);
                effectiveField = { ...f, options: personDeals.map(d => ({ value: d.id, label: d.title })) };
              }

              if (isLocked) {
                return (
                  <div key={f.field} className="nx-form-field">
                    <label className="nx-form-label">{f.label}</label>
                    <div className="nx-form-ctx-field">
                      <span className="nx-form-ctx-value">{getCtxDisplayValue(f.field, form.values[f.field], store)}</span>
                      <span className="nx-form-ctx-source">From {context.source === 'deal' ? 'Deal' : 'Person'}</span>
                    </div>
                  </div>
                );
              }

              return (
                <FormField
                  key={f.field}
                  field={effectiveField}
                  value={form.values[f.field]}
                  values={form.values}
                  error={errors[f.field]}
                  currentRecordId={recordIn?.id}
                  onBlur={(val) => {
                    if (f.field === 'phone' && type === 'people' && val && val.trim()) {
                      const phoneChanged = isCreate || val.trim() !== (recordIn?.phone || '').trim();
                      if (phoneChanged) {
                        const err = validatePhone(val.trim());
                        setErrors(e => err ? { ...e, phone: err } : (() => { const n = { ...e }; delete n.phone; return n; })());
                      }
                    }
                  }}
                  onChange={(v) => {
                    form.setField(f.field, v);
                    if (errors[f.field]) setErrors(e => ({ ...e, [f.field]: null }));
                    if (type === 'companies' && f.field === 'lifecycle' && v !== 'Account') {
                      form.setField('parentCompanyId', null);
                    }
                    if (type === 'deals' && f.field === 'pipelineId') {
                      const all = window.__nx_store?.allStages || [];
                      const first = all.find(s => s.pipelineId === v);
                      form.setField('stageId', first?.id || '');
                    }
                  }}
                />
              );
            })}
          </div>
        </div>
        <div className="nx-form-drawer-foot">
          <span className="nx-muted nx-mini">{form.dirty ? 'Unsaved changes' : 'No changes'}</span>
          <span style={{ flex: 1 }} />
          <Btn variant="ghost" onClick={close}>Cancel</Btn>
          <Btn variant="primary" icon="Check" onClick={submit}>{isCreate ? 'Create' : 'Save Changes'}</Btn>
        </div>
      </aside>
    </>
  );
};

const FormField = ({ field, value, values, error, onChange, onBlur, currentRecordId }) => {
  const opts = typeof field.options === 'function' ? field.options(values) : field.options;
  return (
    <div className={'nx-form-field' + (error ? ' is-error' : '')}>
      <label className="nx-form-label">
        {field.label}
        {field.required && <span className="nx-form-req">*</span>}
      </label>
      {field.type === 'text' || field.type === 'email' ? (
        <input className="nx-input" type={field.type} value={value || ''} placeholder={field.placeholder} onChange={e => onChange(e.target.value)} />
      ) : field.type === 'phone' ? (
        <input
          className="nx-input"
          type="tel"
          value={value || ''}
          placeholder={field.placeholder || '+1 (000) 000-0000'}
          onChange={e => onChange(e.target.value)}
          onBlur={e => onBlur && onBlur(e.target.value)}
        />
      ) : field.type === 'textarea' ? (
        <textarea className="nx-input nx-textarea" value={value || ''} placeholder={field.placeholder} rows={4} onChange={e => onChange(e.target.value)} />
      ) : field.type === 'date' ? (
        <input className="nx-input" type="date" value={value || ''} onChange={e => onChange(e.target.value)} />
      ) : field.type === 'number' ? (
        <input className="nx-input" type="number" value={value ?? ''} min={field.min} max={field.max} onChange={e => onChange(e.target.value)} />
      ) : field.type === 'select' ? (
        <select className="nx-input" value={value || ''} onChange={e => onChange(e.target.value)}>
          <option value="">Select…</option>
          {opts.map(o => {
            const v = typeof o === 'object' ? o.value : o;
            const l = typeof o === 'object' ? o.label : o;
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
      ) : field.type === 'company-picker' ? (
        <CompanyPicker value={value} onChange={onChange} placeholder={field.placeholder} hasError={!!error} />
      ) : field.type === 'enterprise-picker' ? (
        <CompanyPicker
          value={value}
          onChange={onChange}
          placeholder="Search Enterprise companies…"
          hasError={!!error}
          filterFn={c => c.lifecycle === 'Enterprise'}
          excludeId={currentRecordId}
        />
      ) : field.type === 'tags' ? (
        <TagChipInput value={value} onChange={onChange} hasError={!!error} />
      ) : null}
      {error && <span className="nx-form-error">{error}</span>}
      {!error && field.hint && <span style={{ fontSize: 11, color: 'var(--nx-text-faint)', lineHeight: 1.4, display: 'block', marginTop: 3 }}>{field.hint}</span>}
    </div>
  );
};

/* ---------- TagChipInput ---------- */
const TagChipInput = ({ value, onChange, hasError }) => {
  const tags = Array.isArray(value) ? value : (value ? String(value).split(',').map(s => s.trim()).filter(Boolean) : []);
  const [input, setInput] = useState('');
  const inputRef = useRef();

  const commit = (raw) => {
    const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...tags];
    parts.forEach(t => {
      const lower = t.toLowerCase();
      if (!next.some(x => x.toLowerCase() === lower)) next.push(t);
    });
    onChange(next);
    setInput('');
  };

  const remove = (idx) => {
    onChange(tags.filter((_, i) => i !== idx));
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(input);
    } else if (e.key === 'Backspace' && !input && tags.length) {
      remove(tags.length - 1);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    if (v.includes(',')) { commit(v); } else { setInput(v); }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    commit(e.clipboardData.getData('text'));
  };

  return (
    <div
      className={'nx-tag-chip-wrap' + (hasError ? ' is-error' : '')}
      onClick={() => inputRef.current?.focus()}
      role="group"
      aria-label="Tags"
    >
      {tags.map((t, i) => (
        <span key={i} className="nx-tag-chip">
          <span className="nx-tag-chip-label">{t}</span>
          <button
            type="button"
            className="nx-tag-chip-remove"
            onClick={(e) => { e.stopPropagation(); remove(i); }}
            aria-label={`Remove ${t}`}
          >
            <NexusIcon name="X" size={9} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="nx-tag-chip-input"
        value={input}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={tags.length === 0 ? 'Add tags…' : ''}
        aria-label="New tag"
      />
    </div>
  );
};

/* ---------- CompanyPicker ---------- */
export const CompanyPicker = ({ value, onChange, placeholder, hasError, filterFn, excludeId }) => {
  const store = useStore();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [popStyle, setPopStyle] = useState({});
  const [focusIdx, setFocusIdx] = useState(-1);
  const [q, setQ] = useState('');
  const [mini, setMini] = useState(null);
  const wrapRef = useRef();
  const btnRef = useRef();
  const inputRef = useRef();

  const companies = (store.companies || []).filter(c => {
    if (excludeId && c.id === excludeId) return false;
    if (filterFn && !filterFn(c)) return false;
    return true;
  });
  const selected = (store.companies || []).find(c => c.id === value);

  useEffect(() => { setFocusIdx(-1); }, [q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  /* Close picker whenever any ancestor scrolls — prevents popup from drifting off-anchor */
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [open]);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  /* Compute viewport-relative position for the fixed popup */
  const computePopStyle = () => {
    if (!btnRef.current) return { position: 'fixed', top: 0, left: 0, width: 260 };
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const popW = Math.max(r.width, 260);

    /* Right-edge collision: shift left if popup would overflow viewport */
    let left = r.left;
    if (left + popW > vw - 8) left = Math.max(8, vw - popW - 8);

    /* Vertical: prefer opening below; flip above when below has less room */
    const spaceBelow = vh - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const maxPopH = 340;

    if (spaceBelow >= 160 || spaceBelow >= spaceAbove) {
      return {
        position: 'fixed',
        top: r.bottom + 4,
        left,
        width: popW,
        minWidth: 0,
        maxHeight: Math.min(maxPopH, Math.max(spaceBelow, 120)),
      };
    }
    return {
      position: 'fixed',
      bottom: vh - r.top + 4,
      left,
      width: popW,
      minWidth: 0,
      maxHeight: Math.min(maxPopH, Math.max(spaceAbove, 120)),
    };
  };

  const openPicker = () => {
    const newStyle = computePopStyle();
    setPopStyle(newStyle);
    setOpen(o => !o);
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return companies.slice(0, 50);
    return companies.filter(c =>
      (c.name || '').toLowerCase().includes(ql) ||
      (c.domain || '').toLowerCase().includes(ql) ||
      (c.industry || '').toLowerCase().includes(ql)
    ).slice(0, 50);
  }, [companies, q]);

  const pick = (c) => { onChange(c.id); setOpen(false); setQ(''); setFocusIdx(-1); };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const target = focusIdx >= 0 ? filtered[focusIdx] : filtered[0];
      if (target) pick(target);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const openMiniCreate = () => { setOpen(false); setMini({ name: q.trim() || '' }); };
  const saveMini = (payload) => {
    if (!payload.name || !payload.name.trim()) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Company name required' });
      return;
    }
    const newId = store.create('companies', { ...payload, deals: 0, lastActivity: 'just now' });
    if (newId) {
      onChange(newId);
      toast({ tone: 'success', icon: 'Building', title: 'Company Created', desc: payload.name });
    }
    setMini(null);
  };

  return (
    <div ref={wrapRef} className="nx-comp-picker" style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        className={'nx-input nx-comp-picker-btn' + (hasError ? ' is-error' : '') + (open ? ' is-open' : '')}
        onClick={openPicker}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', textAlign: 'left', width: '100%', cursor: 'pointer', overflow: 'hidden' }}
      >
        {selected ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <NexusIcon name="Building" size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, minWidth: 0 }}>{selected.name}</span>
            {selected.industry && <span className="nx-muted nx-mini" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90 }}>· {selected.industry}</span>}
          </span>
        ) : (
          <span style={{ color: 'var(--nx-text-muted)' }}>{placeholder || 'Search companies…'}</span>
        )}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {selected && (
            <span role="button" tabIndex={0} className="nx-icon-btn" onClick={(e) => { e.stopPropagation(); onChange(null); }} title="Clear" style={{ display: 'inline-flex' }}>
              <NexusIcon name="X" size={11} />
            </span>
          )}
          <NexusIcon name="ChevronDown" size={12} style={{ color: 'var(--nx-text-muted)' }} />
        </span>
      </button>

      {open && (
        <div className="nx-comp-picker-pop" style={popStyle} role="listbox">
          <div className="nx-comp-picker-search">
            <NexusIcon name="Search" size={13} />
            <input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Type to search companies…"
              aria-label="Search companies"
              onKeyDown={handleSearchKeyDown}
            />
            {q && <button className="nx-icon-btn" onClick={() => setQ('')} aria-label="Clear search"><NexusIcon name="X" size={11} /></button>}
          </div>
          <div className="nx-comp-picker-list">
            {filtered.length === 0 && (
              <div className="nx-comp-picker-empty">No companies match "{q}".</div>
            )}
            {filtered.map((c, idx) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={c.id === value}
                className={'nx-comp-picker-item' + (c.id === value ? ' is-active' : '') + (idx === focusIdx ? ' is-focused' : '')}
                onClick={() => pick(c)}
              >
                <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 11 }}>{(c.name || '?')[0]}</span>
                <span className="nx-comp-picker-meta">
                  <span className="nx-comp-picker-name">{c.name}</span>
                  <span className="nx-muted nx-mini">{c.industry || '—'}{c.domain ? ' · ' + c.domain : ''}</span>
                </span>
                {c.id === value && <NexusIcon name="Check" size={12} style={{ color: 'var(--nx-accent)' }} />}
              </button>
            ))}
          </div>
          <button type="button" className="nx-comp-picker-add" onClick={openMiniCreate}>
            <NexusIcon name="Plus" size={12} />
            <span>Add New Company{q.trim() ? ' "' + q.trim() + '"' : ''}</span>
          </button>
        </div>
      )}

      {mini && <QuickCompanyModal initial={mini} onCancel={() => setMini(null)} onSave={saveMini} />}
    </div>
  );
};

/* ---------- QuickCompanyModal ---------- */
export const QuickCompanyModal = ({ initial, onCancel, onSave }) => {
  const [form, setForm] = useState({
    name: initial.name || '', domain: '', industry: 'Software', location: '', lifecycle: 'Lead', parentCompanyId: null,
  });
  const [err, setErr] = useState('');
  const store = useStore();
  const enterprises = (store?.companies || []).filter(c => c.lifecycle === 'Enterprise');
  const submit = () => {
    if (!form.name.trim()) { setErr('Company name is required'); return; }
    const payload = { ...form };
    if (payload.lifecycle !== 'Account') payload.parentCompanyId = null;
    onSave(payload);
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="nx-drawer-veil" style={{ zIndex: 90, alignItems: 'center', justifyContent: 'center', display: 'flex' }} onClick={onCancel}>
      <div className="nx-quick-modal" onClick={e => e.stopPropagation()}>
        <div className="nx-quick-modal-head">
          <span className="nx-quick-modal-icon"><NexusIcon name="Building" size={14} /></span>
          <div>
            <h3 className="nx-quick-modal-title">Add New Company</h3>
            <p className="nx-quick-modal-sub">Saves instantly and links to this person</p>
          </div>
          <button type="button" className="nx-icon-btn" onClick={onCancel} style={{ marginLeft: 'auto' }}><NexusIcon name="X" size={13} /></button>
        </div>
        <div className="nx-quick-modal-body">
          <div className={'nx-form-field' + (err ? ' is-error' : '')}>
            <label className="nx-form-label">Company Name <span className="nx-form-req">*</span></label>
            <input autoFocus className="nx-input" value={form.name} placeholder="Acme Inc." onChange={e => { setForm(f => ({ ...f, name: e.target.value })); if (err) setErr(''); }} />
            {err && <span className="nx-form-error">{err}</span>}
          </div>
          <div className="nx-form-field">
            <label className="nx-form-label">Lifecycle</label>
            <select className="nx-input" value={form.lifecycle} onChange={e => setForm(f => ({ ...f, lifecycle: e.target.value, parentCompanyId: null }))}>
              <option value="Lead">Lead</option>
              <option value="Account">Account</option>
              <option value="Enterprise">Enterprise</option>
            </select>
          </div>
          {form.lifecycle === 'Account' && enterprises.length > 0 && (
            <div className="nx-form-field">
              <label className="nx-form-label">Parent Enterprise <span className="nx-muted nx-mini">(optional)</span></label>
              <select className="nx-input" value={form.parentCompanyId || ''} onChange={e => setForm(f => ({ ...f, parentCompanyId: e.target.value || null }))}>
                <option value="">None</option>
                {enterprises.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          )}
          <div className="nx-form-field">
            <label className="nx-form-label">Domain</label>
            <input className="nx-input" value={form.domain} placeholder="acme.com" onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} />
          </div>
          <div className="nx-form-field">
            <label className="nx-form-label">Industry</label>
            <select className="nx-input" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}>
              {['Software', 'Fintech', 'BFSI', 'Commerce', 'Media', 'Design Tools', 'CRM', 'Healthcare', 'Manufacturing', 'Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="nx-form-field">
            <label className="nx-form-label">Location</label>
            <input className="nx-input" value={form.location} placeholder="Bangalore" onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
          </div>
        </div>
        <div className="nx-quick-modal-foot">
          <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
          <Btn variant="primary" icon="Check" onClick={submit}>Create Company</Btn>
        </div>
      </div>
    </div>
  );
};
